// src/services/auth.js
import axios from 'axios';
const API_BASE_URL = process.env.REACT_APP_API_URL;

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Réduit de 60s à 30s (plus raisonnable)
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
});

// Intercepteur pour ajouter le token automatiquement
apiClient.interceptors.request.use(
  (config) => {
    // Ajouter le token depuis localStorage si disponible
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 🔥 NOUVEAU : Intercepteur pour gérer les erreurs de réponse
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Gérer les erreurs 401 (non autorisé)
    if (error.response?.status === 401) {
      // Nettoyer les données locales
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('businessData');
      
      // Rediriger vers la page de login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?session=expired';
      }
    }
    return Promise.reject(error);
  }
);

// Service d'authentification
export const authService = {
  // 🔐 Vérification MFA
  checkMFAStatus: async (email, password) => {
    const endpoints = ["/bo/login-mfa", "/api/login-mfa", "/auth/login-mfa"];
    
    for (const endpoint of endpoints) {
      try {
        const response = await apiClient.post(endpoint, { 
          email: email.trim().toLowerCase(), 
          password 
        }, { timeout: 10000 });
        
        console.log(`MFA check ${endpoint}:`, response.data);
        return response.data.has_mfa || "NO";
      } catch (err) {
        console.log(`MFA check ${endpoint} error:`, err.response?.status);
        
        // Si c'est une erreur 422 ou 401, vérifier si has_mfa est dans la réponse
        if (err.response?.status === 422 || err.response?.status === 401) {
          if (err.response.data?.has_mfa !== undefined) {
            return err.response.data.has_mfa;
          }
          // Si les identifiants sont incorrects, arrêter la boucle
          return "NO";
        }
      }
    }
    
    return "NO";
  },

  // 🔑 Connexion standard
  performStandardLogin: async (email, password) => {
    const endpoints = ["/bo/login", "/api/login", "/auth/login"];
    
    for (const endpoint of endpoints) {
      try {
        console.log(`Tentative de login sur ${endpoint}`);
        
        const response = await apiClient.post(endpoint, { 
          email: email.trim().toLowerCase(), 
          password 
        }, { timeout: 15000 });
        
        const data = response.data;
        
        // Vérifier la structure de la réponse
        if (!data || (!data.access_token && !data.token) || !data.user) {
          console.error('Réponse login invalide:', data);
          throw new Error("Structure de réponse invalide");
        }

        const token = data.access_token || data.token;
        
        // Stocker les données
        localStorage.setItem('authToken', token);
        localStorage.setItem('userData', JSON.stringify(data.user));
        
        if (data.business) {
          localStorage.setItem('businessData', JSON.stringify(data.business));
        }
        
        if (data.refresh_token) {
          localStorage.setItem('refreshToken', data.refresh_token);
        }
        
        // Ajouter la date d'expiration si disponible
        if (data.expires_in) {
          const expiryTime = Date.now() + (data.expires_in * 1000);
          localStorage.setItem('tokenExpiry', expiryTime.toString());
        }

        console.log('Login réussi sur', endpoint);
        
        return {
          token,
          user: data.user,
          business: data.business,
          refreshToken: data.refresh_token || "",
          expiresIn: data.expires_in
        };
        
      } catch (err) {
        console.error(`Erreur login ${endpoint}:`, err.response?.status || err.message);
        
        // Si c'est une erreur d'authentification, arrêter la boucle
        if (err.response?.status === 401 || err.response?.status === 422) {
          throw new Error(err.response?.data?.message || "Identifiants incorrects");
        }
        
        // Pour les autres erreurs, continuer avec le prochain endpoint
        if (endpoint !== endpoints[endpoints.length - 1]) {
          continue;
        } else {
          throw new Error(err.response?.data?.message || "Échec de la connexion");
        }
      }
    }
    
    throw new Error("Impossible de se connecter, veuillez réessayer");
  },

  // 🔄 Rafraîchir le token
  refreshToken: async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error("Aucun token de rafraîchissement disponible");
      }
      
      const response = await apiClient.post('/auth/refresh', {
        refresh_token: refreshToken
      }, { timeout: 10000 });
      
      const data = response.data;
      
      if (!data.access_token) {
        throw new Error("Token de rafraîchissement invalide");
      }
      
      localStorage.setItem('authToken', data.access_token);
      
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }
      
      return data.access_token;
    } catch (error) {
      console.error('Erreur rafraîchissement token:', error);
      
      // En cas d'erreur, nettoyer et déconnecter
      authService.clearStoredData();
      throw error;
    }
  },

  // 📡 Test de connectivité
  testConnectivity: async () => {
    const endpoints = [
      "/bo/login-mfa",
      "/api/login-mfa", 
      "/auth/login-mfa",
      "/bo/login",
      "/api/login",
      "/auth/login"
    ];
    
    const results = [];
    
    for (const endpoint of endpoints) {
      try {
        const startTime = Date.now();
        await apiClient.options(endpoint, { timeout: 5000 });
        const responseTime = Date.now() - startTime;
        results.push(`${endpoint}: ✅ OPTIONS OK (${responseTime}ms)`);
      } catch (err) {
        const status = err.response?.status;
        if (status === 404) {
          results.push(`${endpoint}: ❌ 404 Not Found`);
        } else if (status === 405) {
          results.push(`${endpoint}: ✅ EXISTS (405 Method Not Allowed)`);
        } else if (err.code === 'NETWORK_ERROR' || err.message.includes('Network Error')) {
          results.push(`${endpoint}: 🚫 NETWORK ERROR`);
        } else if (err.message.includes('CORS')) {
          results.push(`${endpoint}: ⚠️ CORS ERROR`);
        } else if (err.code === 'ECONNABORTED') {
          results.push(`${endpoint}: ⏱️ TIMEOUT`);
        } else {
          results.push(`${endpoint}: ⚠️ ${status || err.message}`);
        }
      }
    }
    
    return results;
  },

  // 👤 Récupérer les données depuis localStorage (version sécurisée)
  getStoredData: () => {
    try {
      const token = localStorage.getItem('authToken');
      const userDataStr = localStorage.getItem('userData');
      const businessDataStr = localStorage.getItem('businessData');

      if (!token) {
        return null;
      }

      const result = { token };
      
      // Parser les données utilisateur si disponibles
      if (userDataStr && userDataStr !== 'undefined' && userDataStr !== 'null') {
        try {
          result.user = JSON.parse(userDataStr);
        } catch (e) {
          console.warn('Erreur parsing userData:', e);
        }
      }
      
      // Parser les données business si disponibles
      if (businessDataStr && businessDataStr !== 'undefined' && businessDataStr !== 'null') {
        try {
          result.business = JSON.parse(businessDataStr);
        } catch (e) {
          console.warn('Erreur parsing businessData:', e);
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error reading stored data:', error);
      return null;
    }
  },

  // 🔍 Vérifier si le token est encore valide
  checkTokenValidity: () => {
    try {
      const expiry = localStorage.getItem('tokenExpiry');
      if (!expiry) return true; // Pas d'expiration définie
      
      const now = Date.now();
      const expiryTime = parseInt(expiry, 10);
      
      // Considérer le token comme expiré 5 minutes avant l'heure réelle
      return now < (expiryTime - 300000);
    } catch (error) {
      console.error('Erreur vérification validité token:', error);
      return false;
    }
  },

  // 🧹 Nettoyage complet
  clearStoredData: () => {
    try {
      const itemsToRemove = [
        'authToken',
        'refreshToken',
        'userData',
        'businessData',
        'tokenExpiry'
      ];
      
      itemsToRemove.forEach(item => localStorage.removeItem(item));
    } catch (error) {
      console.error('Erreur nettoyage localStorage:', error);
    }
  },

  // 🚪 Déconnexion (appel API + nettoyage local)
  logout: async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        // Appeler l'API de déconnexion si disponible
        await apiClient.post('/auth/logout', {}, { 
          timeout: 5000 
        }).catch(() => {
          // Ignorer les erreurs de déconnexion API
        });
      }
    } finally {
      // Toujours nettoyer le localStorage
      authService.clearStoredData();
    }
  }
};